import { DateInput } from '@/components/ui/DateInput'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { LossRunClaimCoverageType } from '@/models/lossRunRequest'
import { CLAIM_COVERAGE_OPTIONS, type ClaimFormState } from './claimForm'

interface ClaimFieldsProps {
  value: ClaimFormState
  onChange: (next: ClaimFormState) => void
  disabled?: boolean
  idPrefix?: string
}

export function ClaimFields({ value, onChange, disabled, idPrefix = 'claim' }: ClaimFieldsProps) {
  const id = (name: string) => `${idPrefix}-${name}`
  function set<K extends keyof ClaimFormState>(field: K, next: ClaimFormState[K]) {
    onChange({ ...value, [field]: next })
  }

  return (
    <FieldGroup>
      <Field>
        <FieldLabel htmlFor={id('coverage')}>Coverage type</FieldLabel>
        <Select
          value={value.coverageType}
          onValueChange={(next) => set('coverageType', next as LossRunClaimCoverageType)}
          disabled={disabled}
        >
          <SelectTrigger id={id('coverage')} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CLAIM_COVERAGE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field>
        <FieldLabel htmlFor={id('incident-date')} optional>
          Incident date
        </FieldLabel>
        <DateInput
          value={value.incidentDate}
          onChange={(date) => set('incidentDate', date)}
          inputProps={{ id: id('incident-date'), disabled }}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={id('count')} optional>
          Claim count
        </FieldLabel>
        <Input
          id={id('count')}
          type="number"
          min={0}
          step={1}
          placeholder="—"
          value={value.claimCount}
          onChange={(event) => set('claimCount', event.target.value)}
          disabled={disabled}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={id('paid')} optional>
          Paid loss ($)
        </FieldLabel>
        <Input
          id={id('paid')}
          type="number"
          min={0}
          step="0.01"
          placeholder="—"
          value={value.paidLoss}
          onChange={(event) => set('paidLoss', event.target.value)}
          disabled={disabled}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={id('reserve')} optional>
          Reserve ($)
        </FieldLabel>
        <Input
          id={id('reserve')}
          type="number"
          min={0}
          step="0.01"
          placeholder="—"
          value={value.reserve}
          onChange={(event) => set('reserve', event.target.value)}
          disabled={disabled}
        />
      </Field>
    </FieldGroup>
  )
}
